import { Controller } from '@hotwired/stimulus'

const ACTIVE_ITEM_CLASS = 'periods_sidebar__item-active'
const ACTIVE_BADGE_CLASS = 'life_events__period-badge-active'
const BADGE = '.life_events__period-badge'
const EVENT_ITEM = '.life_events__item'
const PULSE_CLASS = 'life_events__period-badge-pulse'
const OPEN_CLASS = 'periods-open'
const SCROLL_END_FALLBACK = 1500
const PANEL_CLOSE_FALLBACK = 400
const BADGE_HEIGHT = 32
const SCROLL_DRIVEN = window.CSS?.supports('animation-timeline: view()')

export default class PeriodsTimelineController extends Controller {
  static targets = ['item', 'section', 'progress', 'list', 'indicator', 'panel']
  static values = { open: { type: Boolean, default: true } }

  connect () {
    this.spyPaused = false
    this.primed = false
    this.filled = -1
    this.stickyOffset = this.#readStickyOffset()

    this.onScroll = () => this.#scheduleSpy()
    this.onResize = () => {
      this.stickyOffset = this.#readStickyOffset()
      this.#moveIndicator(this.#activeItem())
    }
    // The list is collapsed on small screens, where an item has no box to measure until it has
    // finished opening; the pill takes its place then.
    this.onPanelOpened = (event) => {
      if (event.propertyName === 'grid-template-rows') this.#moveIndicator(this.#activeItem())
    }
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onResize)
    this.element.addEventListener('transitionend', this.onPanelOpened)

    this.#highlightActive()
    this.#fillRail()
    this.primed = true
  }

  disconnect () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
    this.element.removeEventListener('transitionend', this.onPanelOpened)
    clearTimeout(this.scrollEndTimer)
    if (this.frame) window.cancelAnimationFrame(this.frame)
    this.frame = null

    if (this.hasListTarget) delete this.listTarget.dataset.live
    if (this.hasIndicatorTarget) this.indicatorTarget.removeAttribute('style')
    if (this.hasProgressTarget) this.progressTarget.style.setProperty('--timeline-progress', 0)
  }

  toggle () {
    this.openValue = !this.openValue
  }

  openValueChanged (open) {
    this.element.classList.toggle(OPEN_CLASS, open)
  }

  select ({ currentTarget: item }) {
    const periodId = item.dataset.periodId
    const section = this.#sectionFor(periodId)
    this.#highlight(periodId)
    if (!section) return

    const wasOpen = this.openValue
    this.openValue = false

    const jump = () => {
      this.spyPaused = true
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
      this.#resumeSpyAfterScroll()
    }

    // On a narrow screen the sidebar sits in normal flow above the timeline, so
    // closing its accordion shifts the section's real position as the panel
    // collapses. Jumping before that transition settles computes the target
    // against a height that's already stale by the time the scroll runs. From
    // `lg` up the panel is never collapsed (it's always `display: block`, open
    // or not), so there's nothing to wait for there.
    if (wasOpen && this.#isAccordion()) this.#afterPanelCloses(jump)
    else jump()
  }

  #isAccordion () {
    return this.hasPanelTarget && getComputedStyle(this.panelTarget).display === 'grid'
  }

  #afterPanelCloses (callback) {
    const onEnd = (event) => {
      if (event.propertyName !== 'grid-template-rows') return
      clearTimeout(timer)
      this.element.removeEventListener('transitionend', onEnd)
      callback()
    }

    const timer = setTimeout(() => {
      this.element.removeEventListener('transitionend', onEnd)
      callback()
    }, PANEL_CLOSE_FALLBACK)

    this.element.addEventListener('transitionend', onEnd)
  }

  #resumeSpyAfterScroll () {
    const resume = () => {
      this.spyPaused = false
      clearTimeout(this.scrollEndTimer)
      window.removeEventListener('scrollend', resume)
    }

    clearTimeout(this.scrollEndTimer)
    window.addEventListener('scrollend', resume)
    this.scrollEndTimer = setTimeout(resume, SCROLL_END_FALLBACK)
  }

  #scheduleSpy () {
    if (this.frame || this.spyPaused) return
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.#fillRail()
      this.#highlightActive()
    })
  }

  #highlightActive () {
    const active = this.sectionTargets.findLast((section, index, sections) => {
      const previous = index > 0 ? sections[index - 1] : null
      const earlyBy = previous ? this.#lastItemHeight(previous) : 0

      return section.getBoundingClientRect().top <= this.stickyOffset + earlyBy
    }) ?? this.sectionTargets[0]

    this.#highlight(active?.dataset.periodId)
  }

  #highlight (periodId) {
    if (!periodId) return

    this.itemTargets.forEach((item) => {
      const active = item.dataset.periodId === periodId
      item.classList.toggle(ACTIVE_ITEM_CLASS, active)
      if (active) this.#moveIndicator(item)
    })
    this.sectionTargets.forEach((section) => {
      const badge = section.querySelector(BADGE)
      if (!badge) return

      const active = section.dataset.periodId === periodId

      if (active && this.primed && !badge.classList.contains(ACTIVE_BADGE_CLASS)) this.#pulse(badge)
      badge.classList.toggle(ACTIVE_BADGE_CLASS, active)
    })
  }

  #activeItem () {
    return this.itemTargets.find((item) => item.classList.contains(ACTIVE_ITEM_CLASS))
  }

  #moveIndicator (item) {
    if (!item || !this.hasIndicatorTarget || !this.hasListTarget) return
    if (!item.offsetHeight) return

    const indicator = this.indicatorTarget
    indicator.style.width = `${item.offsetWidth}px`
    indicator.style.height = `${item.offsetHeight}px`
    indicator.style.transform = `translate(${item.offsetLeft}px, ${item.offsetTop}px)`

    if (!('live' in this.listTarget.dataset)) {
      indicator.getBoundingClientRect()
      this.listTarget.dataset.live = ''
    }
  }

  #pulse (badge) {
    badge.classList.remove(PULSE_CLASS)
    badge.getBoundingClientRect()
    badge.classList.add(PULSE_CLASS)
    badge.addEventListener('animationend', () => badge.classList.remove(PULSE_CLASS), { once: true })
  }

  #fillRail () {
    if (!this.hasProgressTarget || SCROLL_DRIVEN) return

    const rail = this.progressTarget
    const height = rail.offsetHeight
    if (!height) return

    const line = this.stickyOffset + BADGE_HEIGHT
    const filled = Math.min(Math.max(line - rail.getBoundingClientRect().top, 0), height)
    if (Math.abs(filled - this.filled) < 0.5) return

    this.filled = filled
    rail.style.setProperty('--timeline-progress', filled / height)
  }

  #sectionFor (periodId) {
    return this.sectionTargets.find((section) => section.dataset.periodId === periodId)
  }

  #readStickyOffset () {
    const styles = getComputedStyle(document.documentElement)
    const px = (name) => parseInt(styles.getPropertyValue(name), 10) || 0
    return px('--height-navbar') + px('--gap-main') + 1
  }

  #lastItemHeight (section) {
    const lastItem = section.querySelector(`${EVENT_ITEM}:last-child`)
    return lastItem?.getBoundingClientRect().height ?? 0
  }
}
